import * as React from 'react'
import { useSelector } from 'react-redux'

import './styles.scss'
import ToggleSwitch from './ToggleSwitch'
import { SelectWithLabel } from 'components/Form'
import { SeparatorWithLabel } from 'components/common'
import { MARKETING } from '../../../constants'
import { soundOptions, durationOptions } from '../utils'
import { getStaticImage } from 'utils/statics'
import { lang } from 'utils/helpers'
import flagEnvDisplay from 'constants/flagEnvDisplay'

const SoundConfigurationCard = ({
  channels,
  duration,
  withPreview,
  sound,
  inChatSound,
  channelsSound,
  handleChannelsSound,
  setChecked,
  playSound
}) => {
  const { inchat } = useSelector(store => store.modules)

  const parsedSoundOptions = soundOptions.map(({ label, value }) => ({ label: lang(label), value }))
  const parsedDurationOptions = durationOptions.map(({ label, value }) => ({
    label: lang(`${label} ${lang('dashboard_seconds').toLowerCase()}`),
    value
  }))

  const getChannelsTypesOptions = () => {
    const channelsTypesOptions = []
    Object.keys(MARKETING.channels).forEach(channel => {
      if (!channels.some(it => (it.channel || it.provider) === channel)) return
      channelsTypesOptions.push({
        label: lang(`channels_${channel}`),
        value: channel
      })
    })
    return channelsTypesOptions
  }

  const getChannelType = ({ hub, key }) => {
    const channelsTypesOptions = getChannelsTypesOptions()
    const channelType = hub === 'chatmail' ? hub : key
    const channelTypeOption = channelsTypesOptions.find(it => it.value === channelType) || null
    return channelTypeOption
  }

  const handleChannelType = ({ value, index }) => {
    const channel = channels.find(it => {
      return value === 'chatmail'
        ? it.provider === value
        : it.channel === value
    })
    handleChannelsSound({
      action: 'update',
      index,
      value: {
        key: channel?.channel || '',
        hub: channel?.provider || ''
      }
    })
  }

  const getChannelsOptions = ({ hub, key }) => {
    const channelType = hub === 'chatmail' ? hub : key
    const options = [{ label: lang('dashboard_all'), value: 'all' }]
    channels.forEach(({ id, provider, channel, name, botName, botNameIG, value }) => {
      const innerChannelType = provider === 'chatmail' ? provider : channel
      if (innerChannelType !== channelType) return
      options.push({
        label: name || botName || botNameIG || value,
        value: id
      })
    })
    return options
  }

  const getChannel = ({ hub, key, channelId, channelToken }) => {
    const channelsOptions = getChannelsOptions({ hub, key })
    if (!channelId && !channelToken) return channelsOptions[0]
    const channelOption = channelsOptions.find(it => it.value === channelId) || null
    return channelOption
  }

  const handleChannel = ({ currentData, value, index }) => {
    const channel = channels.find(it => it.id === value)
    const data = {
      key: value !== 'all' ? channel.channel : currentData.key,
      hub: value !== 'all' ? channel.provider : currentData.hub,
      channelId: value !== 'all' ? channel.id : '',
      channelToken: value !== 'all' ? channel.token : ''
    }
    handleChannelsSound({ action: 'update', index, value: data })
  }

  return (
    <div className='sound-configuration-card'>
      <div className='config-section'>
        <div className='title-section'>
          <p className='title'>
            {lang('notifications_view_sound_card_title')}
          </p>
          <p className='subtitle'>
            {lang('notifications_view_sound_card_subtitle')}
          </p>
        </div>
        <SelectWithLabel
          label={lang('dashboard_show-notifications-during')}
          placeholder={lang('dashboard_select')}
          options={parsedDurationOptions}
          value={parsedDurationOptions.find(it => it.value === duration)}
          onChange={({ value }) => setChecked({ fieldName: 'duration', fieldValue: value })}
        />
        <ToggleSwitch
          checked={!!withPreview}
          onChange={({ value }) => setChecked({ fieldName: 'withPreview', fieldValue: value })}
          switchTitle={lang('dashboard_message-preview')}
          switchSubtitle={lang('notifications_view_sound_card_toggle_subtitle')}
        />
        <div className='sound-config'>
          <div className='sound-config__container'>
            <p>{lang('dashboard_default-sounds')}</p>
            <div className='sound-selection'>
              <SelectWithLabel
                label={lang('dashboard_pool-default-sound')}
                placeholder={lang('dashboard_select')}
                options={parsedSoundOptions}
                value={parsedSoundOptions.find(it => it.value === sound)}
                onChange={({ value }) => setChecked({ fieldName: 'sound', fieldValue: value })}
              />
              <div className='title' onClick={() => playSound(sound)}>
                <i className='fa fa-volume-up' />
                <p>{lang('dashboard_play')}</p>
              </div>
            </div>
            {
              inchat.isActive && flagEnvDisplay.displayInChat ? (
                <div className='sound-selection'>
                  <SelectWithLabel
                    label={lang('dashboard_inchat-default-sound')}
                    placeholder={lang('dashboard_select')}
                    options={parsedSoundOptions}
                    value={parsedSoundOptions.find(it => it.value === inChatSound)}
                    onChange={({ value }) => setChecked({ fieldName: 'inChatSound', fieldValue: value })}
                  />
                  <div className='title' onClick={() => playSound(inChatSound)}>
                    <i className='fa fa-volume-up' />
                    <p>{lang('dashboard_play')}</p>
                  </div>
                </div>
              ) : null
            }
          </div>
          <SeparatorWithLabel title={lang('dashboard_by-channel')} />
          <div className='sound-config__container'>
            {channelsSound.map((channelSound, index) => (
              <div key={index} className='sound-config__channel-container'>
                <SelectWithLabel
                  label={lang('dashboard_channel-type')}
                  placeholder={lang('dashboard_select')}
                  options={getChannelsTypesOptions()}
                  value={getChannelType(channelSound)}
                  onChange={({ value }) => handleChannelType({ value, index })}
                />
                <SelectWithLabel
                  label={lang('dashboard_channel')}
                  placeholder={lang('dashboard_select')}
                  options={getChannelsOptions(channelSound)}
                  value={getChannel(channelSound)}
                  onChange={({ value }) => handleChannel({ currentData: channelSound, value, index })}
                />
                <SelectWithLabel
                  label={lang('dashboard_sound')}
                  placeholder={lang('dashboard_select')}
                  options={parsedSoundOptions}
                  value={parsedSoundOptions.find(sound => sound.value === channelSound.sound)}
                  onChange={({ value }) => handleChannelsSound({
                    action: 'update',
                    index,
                    value: { sound: value }
                  })}
                />
                <div className='title' onClick={() => playSound(channelSound.sound)}>
                  <i className='fa fa-volume-up' />
                </div>
                <button
                  className='sound-config__btn'
                  onClick={() => handleChannelsSound({ action: 'remove', index })}
                >
                  <figure>
                    <img src={getStaticImage('/icons/orange-brand/icon-close-slim-dark.svg')} alt={lang('dashboard_add-channel')} />
                  </figure>
                </button>
              </div>
            ))}
            <button
              className='sound-config__btn sound-config__btn--add-channel'
              onClick={() => handleChannelsSound({ action: 'new' })}
            >
              <figure>
                <img src={getStaticImage('/icons/orange-brand/add-circle-icon-primary.svg')} alt={lang('dashboard_add-channel')} />
              </figure>
              <p>{lang('dashboard_add-channel')}</p>
            </button>
          </div>
        </div>
      </div>
      <div className='preview-section'>
        <div className='preview-container'>
          <div className='title'>{lang('dashboard_example')}</div>
          <div className='preview'>
            <div className='logo'>
              <img
                src={getStaticImage('/nixi1-notification-logo-v2.png')}
                alt='Logo'
              />
            </div>
            <div className='info-container'>
              {withPreview ?
                <>
                  <div className='info-title'>{lang('notifications_view_sound_card_preview_info_name')}</div>
                  <div className='info-subtitle'>{lang('notifications_view_sound_card_preview_info_text')}</div>
                </>
                :
                <>
                  <div className='info-subtitle'>{lang('notification_received_message')}</div>
                </>
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SoundConfigurationCard
